import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionService } from '../../../services/subscription.service';
import {
  PaymentProviderEnum,
  type PaymentProviderEnumValue
} from '../../../core/models';

/**
 * Entrada pública de checkout de suscripción.
 * Carga el detalle vía {@code getPublicSubscription} y redirige a la página
 * del proveedor correspondiente:
 * {@code /public/subscriptions/{provider}/{token}}.
 */
@Component({
  selector: 'app-public-subscription-checkout',
  templateUrl: './public-subscription-checkout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class PublicSubscriptionCheckoutComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private subscriptionService = inject(SubscriptionService);

  loading = signal(true);
  error = signal(false);
  errorMessage = signal('');

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token');
    const hash = this.route.snapshot.queryParamMap.get('hash');

    if (!token) {
      this.loading.set(false);
      this.error.set(true);
      this.errorMessage.set('No se encontró el enlace de la suscripción.');
      return;
    }

    this.subscriptionService.getPublicSubscription(token, hash).subscribe({
      next: (res) => {
        const provider = res?.body?.provider;
        const segment = this.toProviderPathSegment(provider);
        const checkoutToken = res?.body?.subscription?.token ?? token;

        if (!segment) {
          this.loading.set(false);
          this.error.set(true);
          this.errorMessage.set('No se pudo determinar el proveedor de pago de la suscripción.');
          return;
        }

        void this.router.navigate(['/public/subscriptions', segment, checkoutToken], {
          replaceUrl: true
        });
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
        this.errorMessage.set('No se pudo cargar la suscripción. Verifica el enlace e intenta de nuevo.');
      }
    });
  }

  private toProviderPathSegment(provider: PaymentProviderEnumValue | string | undefined | null): string | null {
    if (!provider) return null;
    const normalized = String(provider).trim().toUpperCase();
    if (normalized === PaymentProviderEnum.NETPAY) return 'netpay';
    if (normalized === PaymentProviderEnum.PAYCODE) return 'paycode';
    return null;
  }
}
